import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useProfileStore } from '../../stores/useDataStore';
import { uploadImage } from '../../api/media';
import ImageUploader from '../common/ImageUploader';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../stores/useAuthStore';
import UserPhotoAlbum from './UserPhotoAlbum';
import { useModalStore } from '../../stores/useUIStore';

const PhotoSelectionModal = ({
  isOpen,
  onClose,
  modalData, // 🟢 CORRECCIÓN: Los datos ahora vienen encapsulados en modalData
}) => {
  const [activeTab, setActiveTab] = useState('upload'); // 'upload' o 'album'
  const { updateType } = modalData || {}; // 🟢 CORRECCIÓN: Extraemos updateType de modalData
  const updateAvatar = useProfileStore((state) => state.updateAvatar);
  const updateCoverPhoto = useProfileStore((state) => state.updateCoverPhoto);
  const updateAvatarFromAlbum = useProfileStore((state) => state.updateAvatarFromAlbum);
  const updateCoverFromAlbum = useProfileStore((state) => state.updateCoverFromAlbum);
  const profilesCache = useProfileStore((state) => state.profilesCache);
  const currentUser = useAuthStore((state) => state.currentUser);
  const closeModal = useModalStore((state) => state.closeModal);

  const [newImageMedia, setNewImageMedia] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const username = modalData?.username || currentUser?.username;
  const cachedProfile = username ? profilesCache[username.toLowerCase()] : null;

  const modalTitle = updateType === 'avatar' ? 'Actualizar Foto de Perfil' : 'Actualizar Foto de Portada';
  
  const resolveMediaPayload = (media) => {
    if (!media) return null;
    if (typeof media === 'string') return { url: media, publicId: null };
    if (typeof media === 'object') {
      const url = media.url || media.path || media.secure_url || '';
      if (!url) return null;
      return {
        url,
        publicId: media.publicId || media.public_id || null,
      };
    }
    return null;
  };

  const handleUploadSuccess = useCallback(async (media) => {
    setNewImageMedia(media);
    // Al subir una nueva imagen, la seleccionamos automáticamente para guardarla.
    // 🟢 SOLUCIÓN: Llamamos a la lógica de envío directamente desde aquí.
    // Esto asegura que la imagen se actualice tan pronto como se suba.
    await handleSubmit(media);
  }, []);

  const handleAlbumSelect = async (media) => {
    const resolvedUrl = typeof media === 'string'
      ? media
      : (media?.url || media?.path || media?.secure_url || '');

    if (!resolvedUrl) {
      toast.error('Foto de album invalida.');
      return;
    }

    setIsSubmitting(true);
    try {
      if (updateType === 'avatar') {
        await updateAvatarFromAlbum(resolvedUrl);
      } else {
        await updateCoverFromAlbum(resolvedUrl);
      }
      toast.success('¡Imagen actualizada con éxito!');
      handleClose();
    } catch (error) {
      console.error('Error al actualizar la imagen desde album:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (imageToSubmit) => {
    const mediaPayload = resolveMediaPayload(imageToSubmit);
    if (!mediaPayload) {
      toast.error('Por favor, selecciona o sube una imagen.');
      return;
    }

    setIsSubmitting(true);
    try {
      // 🟢 SOLUCIÓN: Usamos `updateType` para decidir qué acción del store llamar
      // y envolvemos la URL en el objeto que el backend espera.
      if (updateType === 'avatar') {
        await updateAvatar({ avatar: mediaPayload });
      } else {
        await updateCoverPhoto({ coverPhoto: mediaPayload });
      }
      toast.success('¡Imagen actualizada con éxito!');
      handleClose(); // Cerramos el modal
    } catch (error) {
      console.error('Error al actualizar la imagen:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setNewImageMedia(null);
    setActiveTab('upload');
    if (typeof onClose === 'function') {
      onClose();
    }
    closeModal('photoSelection');
  };

  useEffect(() => {
    if (!isOpen) {
      setNewImageMedia(null);
      setActiveTab('upload');
    }
  }, [isOpen]);

  const postMedia = useMemo(() => {
    const docs = cachedProfile?.pagination?.docs || [];
    return docs.flatMap((post) =>
      (post.images || []).map((url) => ({
        url,
        isPublic: post.privacy === 'public',
        createdAt: post.createdAt,
        sourceId: post._id,
      }))
    );
  }, [cachedProfile?.pagination?.docs]);

  const clipMedia = useMemo(() => {
    const clips = cachedProfile?.user?.clips || [];
    return clips.map((clip) => ({
      url: clip.url,
      isPublic: clip.privacy === 'public',
      createdAt: clip.createdAt,
      sourceId: clip._id,
    }));
  }, [cachedProfile?.user?.clips]);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl">
      <h2 className="text-xl font-bold mb-4 px-6 pt-6">{modalTitle}</h2>
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-4 px-6" aria-label="Tabs">
          <button onClick={() => setActiveTab('upload')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'upload' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
            Subir Foto
          </button>
          <button onClick={() => setActiveTab('album')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'album' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
            Tu Album
          </button>
        </nav>
      </div>

      <div className="p-6 h-96 overflow-y-auto">
        {activeTab === 'upload' && (
          <ImageUploader
            label="Sube una nueva imagen"
            uploader={uploadImage}
            onChange={handleUploadSuccess}
            initialImageUrl={null}
          />
        )}
        {activeTab === 'album' && (
          <UserPhotoAlbum
            username={username}
            isOwner={true}
            selectMode
            onSelect={handleAlbumSelect}
            postMedia={postMedia}
            clipMedia={clipMedia}
            showAlbumControls={false}
          />
        )}
      </div>

      <div className="p-4 bg-gray-50 dark:bg-gray-700/50 border-t dark:border-gray-700 flex justify-end">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-semibold rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-60"
          >
          Cancelar
        </button>
      </div>
    </div>
  );
};

export default PhotoSelectionModal;